package com.neurobs.ccabserver.translation.components.translatorselection

import com.neurobs.ccab.db.{LanguageData, TranslatorData}
import com.neurobs.ccab.translation.TranslatorDataView
import com.neurobs.react.semanticui.{UiButtonFactory, UiGridFactory, UiHeaderFactory, UiLoaderFactory, UiMessageFactory}
import com.neurobs.ccabserver.translation.states.TranslatorStateGenerator
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.ReactElement

@react
object TranslatorSelection {

  case class Props(enclosingElement: (ReactElement*) => ReactElement)

  val component = FunctionalComponent[Props] { props =>

    val translatorState = TranslatorStateGenerator.accessState

    props.enclosingElement(
      if (translatorState.awaitingServerTranslators) Seq(UiLoaderFactory.ActiveTextLoader("Loading server languages...")("mini"))
      else translatorState.registeredTranslators match {
          case Left(_) => Seq(UiMessageFactory.BasicErrorMessage(message = "Unable to load server languages"))
          case Right(translators) =>
            Seq[ReactElement](
              UiGridFactory.Grid(
                UiGridFactory.GridColumn(
                  UiHeaderFactory.TextHeader("Select translation language", 4)(("", "white-text"))
                )("three wide center aligned"),
                TranslatorButtonGroup(
                  translators,
                  enclosingElement = UiGridFactory.GridColumn(_)("six wide")
                ),
                LanguageRegistration(
                  enclosingElement = UiGridFactory.GridColumn(_)("seven wide")
                )
              )("middle aligned")
            )
        }
    )

  }
}

@react
object TranslatorButtonGroup {
  case class Props(translators: List[TranslatorDataView], enclosingElement: List[ReactElement] => ReactElement)

  val component = FunctionalComponent[Props] { props =>

    props.enclosingElement(
      props.translators.map(translator => TranslatorButton(translator).withKey(translator.id.toString))
    )
  }
}


@react
object TranslatorButton {
  case class Props(translator: TranslatorDataView)

  val component = FunctionalComponent[Props] { props =>

    val translatorState = TranslatorStateGenerator.accessState

    def selectedTranslatorId = translatorState.selectedTranslator.map(_.id).getOrElse(-1)
    def isSelected = selectedTranslatorId == props.translator.id

    def handleClick = () => translatorState.setSelectedTranslator(props.translator)

    def buttonModifiers = if (isSelected) "primary" else ""

    UiButtonFactory.Button(display = props.translator.languageDisplayName, handleClick = handleClick)(buttonModifiers)
  }
}

@react
object LanguageRegistration {
  case class Props(enclosingElement: (ReactElement*) => ReactElement)

  val component = FunctionalComponent[Props] { props =>

    val translatorState = TranslatorStateGenerator.accessState

    def registering = translatorState.addingTranslator
    def toggleRegistration = () => translatorState.addingTranslatorState.toggle()
    def unregisteredLanguages = translatorState.unregisteredLanguages

    def buttonModifiers = if (registering) "red" else "green"

    def addLanguagesButton = UiButtonFactory.LabeledIconButton(
      handleClick = toggleRegistration,
      display = "Add new language",
      iconName = "add"
    )(buttonModifiers)

    def hideLanguagesButton = UiButtonFactory.IconButton(
      handleClick = toggleRegistration,
      iconName = "close"
    )(buttonModifiers)

    def triggerButton = if (registering) hideLanguagesButton else addLanguagesButton

    if (!registering) {
      props.enclosingElement(Seq(triggerButton))
    } else {
      LanguageRegistrationButtonGroup(
        unregisteredLanguages,
        reactElements => props.enclosingElement(
          reactElements ++ Seq[ReactElement](triggerButton)
        )
      )
    }
  }
}

@react
object LanguageRegistrationButtonGroup {
  case class Props(languages: List[LanguageData], enclosingElement: (ReactElement*) => ReactElement)

  val component = FunctionalComponent[Props] { props =>

    props.enclosingElement(
      props.languages.map(language => LanguageRegistrationButton(language).withKey(language.id.toString))
    )
  }
}

@react
object LanguageRegistrationButton {
  case class Props(language: LanguageData)

  val component = FunctionalComponent[Props] { props =>

    val translatorState = TranslatorStateGenerator.accessState

    def handleClick = () => {
      translatorState.registerTranslator(props.language)
    }

    UiButtonFactory.Button(
      display = props.language.displayName,
      handleClick = handleClick
    )

  }
}
